// Бэк-офис 2.0, фаза 0: вход владельца и дашборд «сегодня».
// Данные — через TanStack Query, ответы валидируются контрактом.
import { StrictMode, useEffect, useState } from 'react';
import { installCrashReporter } from './crash-report';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import { AccountProfile } from '@dastarhan/contracts';
import { LoginRequest, TodayReport, TokenPair } from '@dastarhan/contracts';
import { formatMoney } from '@dastarhan/core';
import { UpdateBanner } from './UpdateBanner';
import './office.css';
import { MenuScreen } from './MenuScreen';
import { SignupScreen } from './SignupScreen';
import { ModifiersScreen } from './ModifiersScreen';
import { HallScreen } from './HallScreen';
import { PromoScreen } from './PromoScreen';
import { ReservationsScreen } from './ReservationsScreen';
import { OrdersLogScreen } from './OrdersLogScreen';
import { ShiftsScreen } from './ShiftsScreen';
import { CardsScreen } from './CardsScreen';
import { SubscriptionBanner, SubscriptionDot, SubscriptionScreen } from './SubscriptionScreen';
import { DevicesScreen } from './DevicesScreen';
import { CalcScreen } from './CalcScreen';

/*
 * Заглушка премиума. Закрывает сервер — здесь мы лишь объясняем, что
 * происходит, вместо пустого экрана с ошибкой доступа.
 */
function ProLock({ what, onSub, note }: { what: string; onSub: () => void; note?: string }) {
  return (
    <section className="pro-lock">
      <h2>{what} — в тарифе «Про»</h2>
      <p>
        {note ?? `Этот раздел считает себестоимость и прибыль по каждому блюду и
        собирает отчёты под ваши вопросы. Он открыт на продвинутом
        тарифе и на пробном периоде.`}
      </p>
      <button className="btn accent" onClick={onSub}>Открыть «Подписку»</button>
    </section>
  );
}
import { DeliveryScreen } from './DeliveryScreen';
import { ReportBuilderScreen } from './ReportBuilderScreen';
import { ReportsScreen } from './ReportsScreen';
import { TeamScreen } from './TeamScreen';
import { InventoryScreen } from './InventoryScreen';
import { StockScreen } from './StockScreen';
import { GuestsScreen } from './GuestsScreen';
import { AuditScreen } from './AuditScreen';
import { KitchenNow } from '@dastarhan/contracts';
import { useAskSheet, useNote } from './ui/ask';

const API = '/api/v1';
const REFRESH = 'dastarhan.office.refresh';
const TOKEN = 'dastarhan.office.token';
const qc = new QueryClient();

const METHOD_RU: Record<string, string> = { CASH: 'Наличные', CARD: 'Карта', KASPI_QR: 'Kaspi QR' };

function Login({ onIn, onSignup }: { onIn: (t: string) => void; onSignup: () => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true); setErr('');
    const body = LoginRequest.safeParse({ email: email.trim(), password });
    if (!body.success) { setErr('Проверьте почту и пароль (от 8 символов)'); setBusy(false); return; }
    try {
      const res = await fetch(`${API}/auth/login`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body.data),
      });
      if (!res.ok) { setErr('Неверная почта или пароль'); return; }
      const pair = TokenPair.parse(await res.json());
      localStorage.setItem(TOKEN, pair.access);
      // Долгий ключ обмена: рабочий токен живёт 15 минут, и без этого
      // офис выбрасывал владельца посреди работы. Раньше ключ приходил
      // и молча терялся — эндпоинт обновления был, а пользоваться
      // им было нечем
      localStorage.setItem(REFRESH, pair.refresh);
      onIn(pair.access);
    } catch { setErr('Нет связи с сервером'); }
    finally { setBusy(false); }
  };

  return (
    <div className="login">
      <span className="eyebrow">Бэк-офис</span>
      <h1>Вход для владельца</h1>
      <label>Почта
        <input type="email" autoComplete="username" value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && submit()} />
      </label>
      <label>Пароль
        <input type="password" autoComplete="current-password" value={password}
          onChange={(e) => setPassword(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && submit()} />
      </label>
      <p className="error">{err}</p>
      <button className="btn accent" style={{ width: 'min(340px, 90vw)' }}
        disabled={busy} onClick={submit}>{busy ? 'Проверяем…' : 'Войти'}</button>
      <button className="btn ghost-link" onClick={onSignup}>
        Ещё нет заведения? Создать за минуту
      </button>
    </div>
  );
}

function useEscCloses() {
  // Все офисные диалоги закрываются кликом по подложке — Esc делает то же
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      const backdrop = document.querySelector<HTMLElement>('.dialog-backdrop');
      if (backdrop) backdrop.click();
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, []);
}

function Shell({ token, onOut, onToken }: {
  token: string; onOut: () => void; onToken: (t: string) => void;
}) {
  const { ask, sheet } = useAskSheet();
  const { note, noteView } = useNote();
  useEscCloses();
  const [tab, setTab] = useState<'dash' | 'menu' | 'mods' | 'hall' | 'promo' | 'resv' | 'reports' | 'shifts' | 'rb' | 'dl' | 'calc' | 'dev' | 'sub' | 'cards' | 'orders' | 'inv' | 'team' | 'stock' | 'guests' | 'audit'>('dash');
  // Пишем открытый раздел: если экран упадёт, отчёт назовёт место
  const [serviceOpen, setServiceOpen] = useState(false);
  useEffect(() => { localStorage.setItem('dastarhan.screen', tab); }, [tab]);

  /**
   * Продление сессии.
   *
   * Рабочий токен живёт 15 минут — это правильно для безопасности,
   * но владелец сидит в отчётах часами и не должен вводить пароль
   * каждые четверть часа. Обновляем заранее, за пять минут до конца:
   * если сеть моргнёт, останется время на вторую попытку.
   *
   * Выход происходит только по кнопке или когда долгий ключ отозван —
   * то есть по решению человека, а не по таймеру.
   */
  useEffect(() => {
    const renew = async () => {
      const refresh = localStorage.getItem(REFRESH);
      if (!refresh) return;
      try {
        const res = await fetch(`${API}/auth/refresh`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh }),
        });
        if (!res.ok) return;   // ключ отозван — доработаем до конца токена
        const pair = await res.json() as { access: string; refresh: string };
        localStorage.setItem(TOKEN, pair.access);
        localStorage.setItem(REFRESH, pair.refresh);
        onToken(pair.access);
      } catch {
        // Связи нет — попробуем в следующий раз, страницу не рвём
      }
    };
    const timer = setInterval(() => void renew(), 10 * 60_000);
    return () => clearInterval(timer);
  }, [onToken]);
  /* Уровень тарифа: premium-разделы показываем только если открыты */
  const sub = useQuery({
    queryKey: ['sub-tier'],
    queryFn: async () => {
      const res = await fetch(`${API}/subscription/status`,
        { headers: { Authorization: `Bearer ${token}` } });
      return res.ok ? (await res.json() as { pro?: boolean; known?: boolean; proNote?: string }) : null;
    },
    staleTime: 5 * 60_000,
  });
  // Нет карточки платформы — не ограничиваем: так работают первые клиенты
  const pro = sub.data?.known === false || sub.data?.pro !== false;

  const profile = useQuery({
    queryKey: ['profile'],
    queryFn: async () => {
      const res = await fetch(`${API}/account/profile`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.status === 401) { onOut(); throw new Error('401'); }
      return AccountProfile.parse(await res.json());
    },
  });
  const tables = profile.data?.features.tables ?? true;

  /**
   * Показывать ли вкладку.
   *
   * Прячем то, чего человеку не открыть: иначе он жмёт «Отчёты»,
   * получает отказ сервера и решает, что сломалось. Пока профиль
   * не пришёл — показываем всё: пустой офис при медленной сети
   * пугает сильнее лишней вкладки
   */
  const can = (perm: string): boolean => {
    const perms = profile.data?.permissions;
    if (!perms) return true;
    return perms[perm] !== 'denied';
  };
  // Формат без зала не должен показывать пустые «Зал» и «Брони»
  useEffect(() => {
    if (!tables && (tab === 'hall' || tab === 'resv')) setTab('dash');
  }, [tables, tab]);
  return (
    <div className="shell">
      <header>
        <h1>{tab === 'dash' ? 'Как идут дела сегодня' : tab === 'menu' ? 'Меню и стоп-лист' : tab === 'mods' ? 'Модификаторы' : tab === 'hall' ? 'Зал и столы' : tab === 'promo' ? 'Акции' : tab === 'resv' ? 'Брони' : tab === 'reports' ? 'Отчёты' : tab === 'shifts' ? 'Смены точки' : tab === 'rb' ? 'Конструктор отчётов' : tab === 'dl' ? 'Доставка' : tab === 'calc' ? 'Калькуляция' : tab === 'dev' ? 'Устройства' : tab === 'sub' ? 'Подписка' : tab === 'cards' ? 'Карты и купоны' : tab === 'team' ? 'Команда' : tab === 'stock' ? 'Склад' : tab === 'guests' ? 'Гости' : tab === 'inv' ? 'Пересчёт склада' : tab === 'orders' ? 'Журнал заказов' : tab === 'audit' ? 'Журнал действий' : 'Журнал'}</h1>
        <nav className="tabs">
          {/* Семнадцать разделов — не плоский список, а пять групп по смыслу:
              владелец ищет «где про деньги» и «где про склад», а не читает
              семнадцать слов подряд. Редкое (настройка заведения) — внизу */}
          <button className={`btn ${tab === 'dash' ? 'accent' : ''}`} onClick={() => setTab('dash')}>Сводка</button>

          <span className="tabs-group">Продажи и гости</span>
          {can('reports.finance') && (
            <>
              <button className={`btn ${tab === 'reports' ? 'accent' : ''}`} onClick={() => setTab('reports')}>Отчёты</button>
              <button className={`btn ${tab === 'shifts' ? 'accent' : ''}`} onClick={() => setTab('shifts')}>Смены</button>
            </>
          )}
          {can('reports.finance') && (
            <button className={`btn ${tab === 'rb' ? 'accent' : ''}`} onClick={() => setTab('rb')}>Конструктор</button>
          )}
          <button className={`btn ${tab === 'promo' ? 'accent' : ''}`} onClick={() => setTab('promo')}>Акции</button>
          {/* Заказы рядом с отчётами: владелец идёт сюда, когда ищет
              конкретный чек или забытый стол, а не сводку за период */}
          <button className={`btn ${tab === 'orders' ? 'accent' : ''}`} onClick={() => setTab('orders')}>Заказы</button>
          <button className={`btn ${tab === 'cards' ? 'accent' : ''}`} onClick={() => setTab('cards')}>Карты и купоны</button>
          <button className={`btn ${tab === 'guests' ? 'accent' : ''}`} onClick={() => setTab('guests')}>Гости</button>

          {(tables) && <span className="tabs-group">Зал</span>}
          {tables && <button className={`btn ${tab === 'hall' ? 'accent' : ''}`} onClick={() => setTab('hall')}>Зал и схема</button>}
          {tables && <button className={`btn ${tab === 'resv' ? 'accent' : ''}`} onClick={() => setTab('resv')}>Брони</button>}
          {!tables && <span className="tabs-group">Заказы</span>}
          <button className={`btn ${tab === 'dl' ? 'accent' : ''}`} onClick={() => setTab('dl')}>Доставка</button>

          <span className="tabs-group">Меню и склад</span>
          <button className={`btn ${tab === 'menu' ? 'accent' : ''}`} onClick={() => setTab('menu')}>Меню</button>
          <button className={`btn ${tab === 'mods' ? 'accent' : ''}`} onClick={() => setTab('mods')}>Модификаторы</button>
          <button className={`btn ${tab === 'calc' ? 'accent' : ''}`} onClick={() => setTab('calc')}>Калькуляция</button>
          {can('stock.writeoff') && (
            <button className={`btn ${tab === 'stock' ? 'accent' : ''}`} onClick={() => setTab('stock')}>Склад</button>
          )}
          {can('stock.count') && (
            <button className={`btn ${tab === 'inv' ? 'accent' : ''}`} onClick={() => setTab('inv')}>Пересчёт</button>
          )}

          <span className="tabs-group">Заведение</span>
          {can('staff.manage') && (
            <button className={`btn ${tab === 'team' ? 'accent' : ''}`} onClick={() => setTab('team')}>Команда</button>
          )}
          <button className={`btn ${tab === 'dev' ? 'accent' : ''}`} onClick={() => setTab('dev')}>Устройства</button>
          <button className={`btn ${tab === 'sub' ? 'accent' : ''}`} onClick={() => setTab('sub')}>Подписка<SubscriptionDot token={token} /></button>
          <button className={`btn ${tab === 'audit' ? 'accent' : ''}`} onClick={() => setTab('audit')}>Журнал</button>

          {/* Настройка заведения и выход — внизу рельса, отдельно от разделов:
              это действия раз-в-месяц, а не места, куда ходят */}
          <span className="tabs-foot" />
          {profile.data?.isOwner && (
            <button className="btn" onClick={() => setServiceOpen(true)}
              title="Сбор за обслуживание">
              Обслуживание{(profile.data.service?.percent ?? 0) > 0
                ? ` ${profile.data.service!.percent}%` : ''}
            </button>
          )}
          {profile.data?.isOwner && (
            <select className="vertical-select" value={profile.data.vertical}
              title="Формат бизнеса"
              onChange={(e) => void (async () => {
                const v = e.target.value;
                const a = await ask({
                  title: 'Сменить формат заведения',
                  sub: 'Касса, экран кухни и вкладки офиса перестроятся под новый формат. Меню, чеки и остатки останутся как есть.',
                  effects: [
                    ['Станет', e.target.selectedOptions[0]?.text ?? v],
                    ['Что перестроится', 'касса, кухня, набор вкладок'],
                    ['Данные', 'сохранятся полностью'],
                    ['Страница', 'перезагрузится сразу'],
                  ],
                  confirmLabel: 'Сменить формат',
                });
                if (!a) return;
                await fetch(`${API}/account/profile`, {
                  method: 'PATCH',
                  headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                  body: JSON.stringify({ vertical: v }),
                });
                window.location.reload();
              })()}>
              <option value="CAFE">Кафе</option>
              <option value="FASTFOOD">Фастфуд</option>
              <option value="SHOP">Магазин</option>
              <option value="SALON">Салон</option>
              <option value="BILLIARD">Бильярд</option>
            </select>
          )}
          {profile.data?.isOwner && (
            <button className="btn" title="Скачать все данные заведения одним JSON"
              onClick={() => {
                void fetch(`${API}/account/export`, { headers: { Authorization: `Bearer ${token}` } })
                  .then(async (r) => {
                    if (!r.ok) { note('Не получилось выгрузить бэкап — попробуйте ещё раз', 'bad'); return; }
                    const blob = await r.blob();
                    const a = document.createElement('a');
                    a.href = URL.createObjectURL(blob);
                    a.download = `dastarhan-backup-${new Date().toISOString().slice(0, 10)}.json`;
                    a.click();
                    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
                  });
              }}>Бэкап</button>
          )}
          <button className="btn" onClick={onOut}>Выйти</button>
        </nav>
      </header>
      <SubscriptionBanner token={token} onOpen={() => setTab('sub')} />
      {serviceOpen && profile.data && (
        <ServiceDialog token={token}
          current={profile.data.service ?? { percent: 0, dineInOnly: true, fromGuests: 0 }}
          onClose={() => setServiceOpen(false)}
          onSaved={() => { setServiceOpen(false); void profile.refetch(); }} />
      )}
      {tab === 'dash' ? <Dashboard token={token} onOut={onOut} /> : tab === 'menu' ? <MenuScreen token={token} /> : tab === 'mods' ? <ModifiersScreen token={token} /> : tab === 'hall' ? <HallScreen token={token} /> : tab === 'promo' ? <PromoScreen token={token} /> : tab === 'resv' ? <ReservationsScreen token={token} /> : tab === 'reports' ? <ReportsScreen token={token} /> : tab === 'shifts' ? <ShiftsScreen token={token} /> : tab === 'rb' ? (pro ? <ReportBuilderScreen token={token} /> : <ProLock what="Конструктор отчётов" note={sub.data?.proNote} onSub={() => setTab('sub')} />) : tab === 'dl' ? <DeliveryScreenWrap token={token} /> : tab === 'calc' ? (pro ? <CalcScreen token={token} /> : <ProLock what="Калькуляция" note={sub.data?.proNote} onSub={() => setTab('sub')} />) : tab === 'dev' ? <DevicesScreenWrap token={token} /> : tab === 'sub' ? <SubscriptionScreen token={token} /> : tab === 'cards' ? <CardsScreen token={token} /> : tab === 'orders' ? <OrdersLogScreen token={token} /> : tab === 'team' ? <TeamScreen token={token} isOwner={profile.data?.isOwner ?? false} /> : tab === 'inv' ? <InventoryScreen token={token} /> : tab === 'stock' ? <StockScreen token={token} /> : tab === 'guests' ? <GuestsScreen token={token} isOwner={profile.data?.isOwner ?? false} /> : <AuditScreen token={token} isOwner={profile.data?.isOwner ?? false} />}
      {sheet}
      {noteView}
    </div>
  );
}

function Dashboard({ token, onOut }: { token: string; onOut: () => void }) {
  const today = useQuery({
    queryKey: ['today'],
    queryFn: async () => {
      const res = await fetch(`${API}/reports/today`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.status === 401) { onOut(); throw new Error('401'); }
      if (!res.ok) throw new Error('HTTP');
      return TodayReport.parse(await res.json());
    },
    refetchInterval: 15_000,
  });
  const kitchen = useQuery({
    queryKey: ['kitchen-now'],
    refetchInterval: 10_000,
    queryFn: async () => KitchenNow.parse(
      await (await fetch(`${API}/reports/kitchen-now`, { headers: { Authorization: `Bearer ${token}` } })).json(),
    ),
  });

  return (
    <>
      {today.isPending && <p className="empty">Считаем…</p>}
      {today.isError && <p className="empty">Не удалось загрузить. Проверьте связь и обновите страницу.</p>}
      {today.data && (
        <>
          <p className="live-line">
            <i className="live-dot" /> живое · обновлено{' '}
            {new Date(today.dataUpdatedAt).toLocaleTimeString('ru-RU')}
          </p>
          <div className="kpis">
            <div className="kpi"><span>Выручка</span><b className="money">{formatMoney(today.data.revenue)}</b></div>
            <div className="kpi"><span>Чеков</span><b>{today.data.checks}</b></div>
            <div className="kpi"><span>Средний чек</span><b className="money">{formatMoney(today.data.avgCheck)}</b></div>
            <div className="kpi"><span>Возвратов</span><b className={today.data.cancelled > 0 ? 'warn' : ''}>{today.data.cancelled}</b></div>
            <div className="kpi"><span>На смене</span><b>{today.data.onShift} чел</b></div>
            <div className="kpi"><span>Открытых смен</span><b>{today.data.openShifts}</b></div>
          </div>
          {(kitchen.data?.rows.length ?? 0) > 0 && (
            <section className="menu-cat" style={{ marginTop: 14 }}>
              <header><h2>Кухня сейчас</h2><span className="rule-tag">{kitchen.data!.rows.length}</span></header>
              {kitchen.data!.rows.map((k) => (
                <div key={k.number} className="menu-row kitchen-row">
                  <b>№{k.number}</b>
                  <span className="hint-inline">{k.table ?? 'с собой'} · {k.items} поз</span>
                  <span className={`k-status s-${k.status}`}>
                    {k.status === 'NEW' ? 'ждёт' : k.status === 'COOKING' ? 'готовится' : 'готов'}
                  </span>
                  <span className={`right ${k.minutes > 20 && k.status !== 'READY' ? 'neg' : 'hint-inline'}`}>
                    {k.minutes} мин
                  </span>
                </div>
              ))}
            </section>
          )}
          {today.data.revenue > 0 && (
            <div className="hours-chart mini" role="img" aria-label="Выручка сегодня по часам">
              {(() => {
                const max = Math.max(...today.data.byHour.map((x) => x.revenue), 1);
                return today.data.byHour.map((x) => (
                  <div key={x.hour} className="hbar-col" title={`${x.hour}:00 — ${formatMoney(x.revenue)}`}>
                    <div className="hbar" style={{ height: `${Math.round((x.revenue / max) * 100)}%` }} />
                    {x.hour % 6 === 0 && <span className="hbar-label">{x.hour}</span>}
                  </div>
                ));
              })()}
            </div>
          )}
          {today.data.byMethod.length > 0 ? (
            <div className="methods-table">
              {today.data.byMethod.map((m) => (
                <div key={m.method}>
                  <span>{METHOD_RU[m.method] ?? m.method}</span>
                  <span className="money">{formatMoney(m.amount)}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="empty">Первый чек с кассы появится здесь сам — обновлять не нужно.</p>
          )}
        </>
      )}
    </>
  );
}

function App() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN));
  const [signup, setSignup] = useState(false);
  if (!token && signup) {
    return <SignupScreen onBack={() => setSignup(false)}
      onDone={(access) => { localStorage.setItem(TOKEN, access); setToken(access); setSignup(false); }} />;
  }
  if (!token) return <Login onIn={setToken} onSignup={() => setSignup(true)} />;
  return (
    <Shell token={token} onToken={setToken}
      onOut={() => {
        localStorage.removeItem(TOKEN);
        localStorage.removeItem(REFRESH);
        setToken(null);
      }} />
  );
}

installCrashReporter('backoffice');

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <UpdateBanner />
    <QueryClientProvider client={qc}>
      <App />
    </QueryClientProvider>
  </StrictMode>,
);

/** Доставка привязана к точке: зоны и курьеры у каждой свои. */
function DeliveryScreenWrap({ token }: { token: string }) {
  const locations = useQuery({
    queryKey: ['locations'],
    queryFn: async () => {
      // Список точек живёт в меню-контроллере — тот же адрес, что у экрана зала
      const r = await fetch('/api/v1/menu/locations', { headers: { Authorization: `Bearer ${token}` } });
      return (await r.json()) as { id: string; name: string }[];
    },
  });
  const loc = locations.data?.[0]?.id;
  if (!loc) return <p className="hint">Загружаем точки…</p>;
  return <DeliveryScreen token={token} locationId={loc} />;
}

/** Устройства привязаны к точке: у каждой свои кассы и экраны. */
function DevicesScreenWrap({ token }: { token: string }) {
  const locations = useQuery({
    queryKey: ['locations'],
    queryFn: async () => {
      const r = await fetch('/api/v1/menu/locations', { headers: { Authorization: `Bearer ${token}` } });
      return (await r.json()) as { id: string; name: string }[];
    },
  });
  const loc = locations.data?.[0]?.id;
  if (!loc) return <p className="hint">Загружаем точки…</p>;
  return <DevicesScreen token={token} locationId={loc} />;
}

/**
 * Сбор за обслуживание.
 *
 * Настройка короткая, но денежная: процент попадает в каждый чек
 * и виден гостю. Поэтому здесь не просто поле, а пример расчёта —
 * владелец должен увидеть, что получится, до того как сохранит.
 */
function ServiceDialog({ token, current, onClose, onSaved }: {
  token: string;
  current: { percent: number; dineInOnly: boolean; fromGuests: number };
  onClose: () => void;
  onSaved: () => void;
}) {
  const [percent, setPercent] = useState(current.percent);
  const [dineInOnly, setDineInOnly] = useState(current.dineInOnly);
  const [fromGuests, setFromGuests] = useState(current.fromGuests);
  const [busy, setBusy] = useState(false);

  const example = 500000;   // счёт 5 000 ₸ — типичный для примера
  const fee = Math.round((example * percent) / 100);

  const save = async () => {
    setBusy(true);
    try {
      await fetch(`${API}/account/service`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ percent, dineInOnly, fromGuests }),
      });
      onSaved();
    } finally { setBusy(false); }
  };

  return (
    <div className="dialog">
      <div className="dialog-card">
        <h2>Сбор за обслуживание</h2>
        <p className="hint">
          Процент добавляется к счёту после скидки и печатается в чеке
          отдельной строкой — гость видит, за что платит.
        </p>

        <label>Процент
          <input type="number" min={0} max={30} value={percent}
            onChange={(e) => setPercent(Math.min(30, Math.max(0, Number(e.target.value))))} />
        </label>

        <label className="check">
          <input type="checkbox" checked={dineInOnly}
            onChange={(e) => setDineInOnly(e.target.checked)} />
          Только в зале (на вынос и доставку не берём)
        </label>

        <label>Начиная с гостей
          <input type="number" min={0} max={30} value={fromGuests}
            onChange={(e) => setFromGuests(Math.max(0, Number(e.target.value)))} />
          <i className="split">0 — берём всегда. Ставят 6, когда сбор только для компаний</i>
        </label>

        {percent > 0 ? (
          <p className="service-example">
            Счёт 5 000 ₸ → обслуживание <b>{Math.round(fee / 100)} ₸</b> →
            {' '}гость платит <b>{Math.round((example + fee) / 100)} ₸</b>
          </p>
        ) : (
          <p className="hint">Сейчас сбор не берётся.</p>
        )}

        <div className="modal-actions">
          <button className="btn" onClick={onClose}>Отмена</button>
          <button className="btn primary" disabled={busy} onClick={() => void save()}>
            Сохранить
          </button>
        </div>
      </div>
    </div>
  );
}